import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { initials } from "../magSession";
import { User, Users, Bell, BellOff, Megaphone, UserCog, Loader2, LayoutDashboard } from "lucide-react";
import { BUILD_TIME } from "../buildInfo";
import { toast } from "sonner";
import { NutzerVerwaltung } from "./NutzerVerwaltung";
import { Rundmail } from "./Rundmail";
import { Profil } from "./Profil";
import { api } from "../api";
import { pushSupported, getPushState, subscribePush, unsubscribePush, isPWA } from "../push";

function fmtBuild(iso) {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function Logo({ testMode }) {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 no-underline">
      <img src="/icons/logo.png" alt="WhatsDown Logo" width={36} height={36}
        style={{ display: "block", objectFit: "contain" }} />
      <span className="flex flex-col" style={{ lineHeight: 1 }}>
        <span className="display-font font-extrabold text-lg">
          <span style={{ color: "#0071B8" }}>Whats</span><span style={{ color: "#F97017" }}>Down</span>
        </span>
        <span className="font-semibold" style={{ color: "#000000", fontSize: "0.62rem", marginTop: 1, letterSpacing: ".02em" }}>Facility Management</span>
      </span>
      {testMode && (
        <span data-testid="nav-test-badge" className="ml-1 px-2 py-0.5 rounded-md text-xs font-extrabold"
          style={{ background: "#1D4ED8", color: "#fff", letterSpacing: ".04em" }}>
          TESTSYSTEM
        </span>
      )}
    </Link>
  );
}

function ProfileMenu({ session, onLogout }) {
  const [open, setOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showRundmail, setShowRundmail] = useState(false);
  const [showProfil, setShowProfil] = useState(false);
  const [pushState, setPushState] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [role, setRole] = useState(() => sessionStorage.getItem("mag_role") || "");
  const [displayName, setDisplayName] = useState(() => sessionStorage.getItem("mag_nick") || session.user_name || "Unbekannt");
  const ref = useRef(null);
  const navigate = useNavigate();
  const token = sessionStorage.getItem("mag_token");
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { if (pushSupported()) getPushState().then(setPushState); }, []);
  useEffect(() => { if (open) setRole(sessionStorage.getItem("mag_role") || ""); }, [open]);
  useEffect(() => {
    if (!token) return;
    api.get("/magauth/profile", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setDisplayName(data.name || session.user_name);
        if (data.nickname) sessionStorage.setItem("mag_nick", data.nickname);
        else sessionStorage.removeItem("mag_nick");
      })
      .catch(() => {});
  }, [token, session.user_name]);
  const name = session.user_name || "Unbekannt";
  const isTest = session.mode === "test";
  const isAdmin = role === "admin";
  const roleLabel = role === "admin" ? "Administrator" : role === "manager" ? "Manager" : role === "janitor" ? "Hausmeister" : "—";

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState === "subscribed") { await unsubscribePush(token); setPushState("default"); toast.success("Benachrichtigungen deaktiviert."); }
      else { await subscribePush(token); setPushState("subscribed"); toast.success("Benachrichtigungen aktiviert."); }
    } catch (e) { toast.error(e?.message || "Benachrichtigungen konnten nicht geändert werden."); }
    finally { setPushBusy(false); }
  };

  const itemStyle = { display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", borderTop: "1px solid #E2E8F0", cursor: "pointer", color: "#0F172A", width: "100%", textAlign: "left" };

  return (
    <>
      <div style={{ position: "relative" }} ref={ref}>
        <button data-testid="profile-menu-trigger" onClick={() => setOpen((v) => !v)}
          title="Mein Profil" aria-label="Mein Profil"
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "#FFF7ED", border: "1px solid #FED7AA", cursor: "pointer" }}>
          <User size={20} color="#F97316" />
        </button>
        {open && (
          <div data-testid="profile-menu-popup" className="absolute mt-2 rounded-2xl"
            style={{ right: 0, width: 300, background: "#fff", boxShadow: "0 16px 40px rgba(15,23,42,.18)", border: "1px solid #E2E8F0", zIndex: 60, overflow: "hidden" }}>
            <div className="px-5 py-3 text-sm font-bold" style={{ background: "#F1F5F9", color: "#475569" }}>Mein Profil</div>
            <div className="p-5">
              <div className="flex items-center gap-4">
                <span className="rounded-full flex items-center justify-center font-extrabold"
                  style={{ width: 56, height: 56, background: "#E2E8F0", color: "#475569", fontSize: 18 }}>{initials(displayName)}</span>
                <div className="min-w-0">
                  <div className="font-extrabold truncate" data-testid="profile-name">{displayName}</div>
                  <div className="text-sm italic truncate" style={{ color: "#64748B" }} data-testid="profile-username">{name}</div>
                </div>
              </div>
              {isTest && (
                <div className="mt-4">
                  <span className="badge-hero" data-testid="profile-system"
                    style={{ background: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>
                    Testsystem
                  </span>
                </div>
              )}
              <p className="text-sm mt-4 mb-0 font-bold" style={{ color: "#475569" }} data-testid="profile-role">Rolle: {roleLabel}</p>
            </div>
            {isAdmin && (
              <button data-testid="profile-dashboard" onClick={() => { navigate("/dashboard"); setOpen(false); }}
                className="px-5 py-3 text-sm font-semibold" style={itemStyle}>
                <LayoutDashboard size={16} color="#F97316" /> Dashboard
              </button>
            )}
            {isAdmin && (
              <button data-testid="profile-manage-users" onClick={() => { setShowUsers(true); setOpen(false); }}
                className="px-5 py-3 text-sm font-semibold" style={itemStyle}>
                <Users size={16} color="#F97316" /> Nutzer verwalten
              </button>
            )}
            <button data-testid="profile-edit-name" onClick={() => { setShowProfil(true); setOpen(false); }}
              className="px-5 py-3 text-sm font-semibold" style={itemStyle}>
              <UserCog size={16} color="#F97316" /> Mein Name
            </button>
            {role === "janitor" && (
              <button data-testid="profile-rundmail" onClick={() => { setShowRundmail(true); setOpen(false); }}
                className="px-5 py-3 text-sm font-semibold" style={itemStyle}>
                <Megaphone size={16} color="#F97316" /> Rundmail / Abwesenheit
              </button>
            )}
            {role === "janitor" && isPWA() && pushSupported() && pushState !== "denied" && (
              <button data-testid="profile-push-toggle" onClick={togglePush} disabled={pushBusy}
                className="px-5 py-3 text-sm font-semibold" style={itemStyle}>
                {pushBusy ? <Loader2 size={16} className="animate-spin" color="#F97316" /> : (pushState === "subscribed" ? <Bell size={16} color="#F97316" /> : <BellOff size={16} color="#94A3B8" />)}
                {pushBusy ? "Wird geändert…" : (pushState === "subscribed" ? "Benachrichtigungen: An" : "Benachrichtigungen aktivieren")}
              </button>
            )}
            <button data-testid="profile-logout" onClick={onLogout}
              className="w-full text-left px-5 py-3 text-sm font-semibold"
              style={{ background: "none", border: "none", borderTop: "1px solid #E2E8F0", cursor: "pointer", color: "#DC2626" }}>
              Abmelden
            </button>
          </div>
        )}
      </div>
      {showUsers && <NutzerVerwaltung onClose={() => setShowUsers(false)} />}
      {showRundmail && <Rundmail onClose={() => setShowRundmail(false)} />}
      {showProfil && <Profil onClose={() => setShowProfil(false)} onSaved={(d) => setDisplayName(d.name || session.user_name)} />}
    </>
  );
}

export function Navbar() {
  const { magSession, logoutMag } = useAuth();
  const navigate = useNavigate();

  const doLogoutMag = () => {
    logoutMag();
    navigate("/login");
  };

  return (
    <header className="navbar-wd">
      <div className="navbar-inner">
        <div className="flex items-center gap-4">
          <Logo testMode={magSession?.mode === "test"} />
        </div>
        <nav className="flex items-center gap-2" data-testid="nav-menu">
          {magSession && <ProfileMenu session={magSession} onLogout={doLogoutMag} />}
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="footer-wd" data-testid="footer">
      <div className="container-wd" style={{ paddingTop: 0, paddingBottom: 0 }}>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm items-center">
          <span className="font-semibold mr-auto">WhatsDown</span>
          <a data-testid="footer-impressum" href="https://www.makrolog.de/home/#/impressum" target="_blank" rel="noreferrer">Impressum</a>
          <a data-testid="footer-datenschutz" href="https://www.makrolog.de/home/#/datenschutz" target="_blank" rel="noreferrer">Datenschutz</a>
          <span data-testid="footer-build" className="text-xs" style={{ color: "#94A3B8" }}>Build: {fmtBuild(BUILD_TIME)} Uhr</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
